import { useEffect, useState } from "react";
import { Sparkles, Search, BarChart3, Compass, Zap, TrendingUp, TrendingDown, ArrowRight, Lightbulb } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip,
  BarChart, Bar,
} from "recharts";
import { api } from "../lib/api";
import BoostDialog from "./BoostDialog";

/**
 * ProInsightsCharts — the deeper analytics gated by Pro Insights ($9.99/mo).
 *
 * Mounted by ProviderAnalyticsCard only when `proInsightsActive` is true on
 * the analytics response. Server-side the same gate is enforced: a non-
 * subscribed provider hitting `/providers/{uid}/analytics/pro` gets 402.
 *
 * Three sections:
 *   1. Daily views line chart
 *   2. Top search-term & source-breakdown tables
 *   3. Per-service revenue funnel (bar chart)
 */
export default function ProInsightsCharts({ providerId, days = 90 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [boostTarget, setBoostTarget] = useState(null);  // {service, tier} pre-fill

  useEffect(() => {
    if (!providerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const { data } = await api.get(`/providers/${providerId}/analytics/pro?days=${days}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Failed to load Pro Insights");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [providerId, days]);

  if (loading) return <div className="text-sm text-foreground/55">Loading Pro Insights…</div>;
  if (err) return <div className="text-sm text-destructive">{err}</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="pro-insights-charts">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-secondary" />
        <span className="label-mystic text-secondary">Pro Insights · last {data.windowDays} days</span>
      </div>

      {/* ── Daily views ──────────────────────────────────────────── */}
      <ChartCard
        icon={<BarChart3 size={13} className="text-primary" />}
        title="Daily profile views"
        subtitle={`${data.dailyViews.reduce((a, r) => a + r.count, 0)} total · ${data.windowDays}-day trend`}
        testid="pi-daily-views"
      >
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.dailyViews} margin={{ top: 6, right: 12, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,181,140,0.12)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "rgba(244,238,218,0.55)" }}
                tickFormatter={(d) => {
                  const dt = new Date(d);
                  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                }}
                minTickGap={24}
                stroke="rgba(212,181,140,0.25)"
              />
              <YAxis
                tick={{ fontSize: 10, fill: "rgba(244,238,218,0.55)" }}
                stroke="rgba(212,181,140,0.25)"
                allowDecimals={false}
                width={28}
              />
              <Tooltip
                contentStyle={{
                  background: "#15102A",
                  border: "1px solid rgba(212,181,140,0.32)",
                  borderRadius: 2,
                  color: "#F4EEDA",
                  fontSize: 12,
                }}
                labelFormatter={(d) =>
                  new Date(d).toLocaleDateString("en-US",
                    { weekday: "short", month: "short", day: "numeric" })
                }
              />
              <Line type="monotone" dataKey="count" stroke="#D4B58C" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* ── Search terms + Source breakdown ──────────────────────── */}
      <div className="grid md:grid-cols-2 gap-4">
        <ChartCard
          icon={<Search size={13} className="text-primary" />}
          title="Top search terms"
          subtitle="What seekers typed before finding you"
          testid="pi-search-terms"
        >
          {data.topSearchTerms.length === 0 ? (
            <EmptyHint>No search-driven visits yet. Once your profile shows up in Discover or Marketplace searches and seekers click through, terms will appear here.</EmptyHint>
          ) : (
            <ul className="space-y-2">
              {data.topSearchTerms.map((t) => (
                <li key={t.term} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-foreground/85 truncate">{t.term}</span>
                  <span className="font-serif text-primary tabular-nums">{t.count}</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>

        <ChartCard
          icon={<Compass size={13} className="text-primary" />}
          title="Where viewers come from"
          subtitle="Referral source breakdown"
          testid="pi-source-breakdown"
        >
          {data.sourceBreakdown.length === 0 ? (
            <EmptyHint>No traffic yet.</EmptyHint>
          ) : (
            <ul className="space-y-2">
              {data.sourceBreakdown.map((s) => (
                <li key={s.source} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-foreground/85 capitalize">{s.source}</span>
                  <span className="font-serif text-primary tabular-nums">{s.count}</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
      </div>

      {/* ── Per-service funnel ───────────────────────────────────── */}
      <ChartCard
        icon={<BarChart3 size={13} className="text-primary" />}
        title="Per-service performance"
        subtitle="Revenue by service in the window"
        testid="pi-service-funnel"
      >
        {data.serviceFunnel.length === 0 ? (
          <EmptyHint>No active services yet. Add one from your profile to start tracking per-service performance.</EmptyHint>
        ) : (
          <>
            <div className="h-44 mb-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.serviceFunnel}
                  margin={{ top: 6, right: 12, left: -10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,181,140,0.12)" />
                  <XAxis
                    dataKey="title"
                    tick={{ fontSize: 10, fill: "rgba(244,238,218,0.55)" }}
                    interval={0}
                    height={36}
                    tickFormatter={(s) => (s.length > 14 ? s.slice(0, 12) + "…" : s)}
                    stroke="rgba(212,181,140,0.25)"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "rgba(244,238,218,0.55)" }}
                    stroke="rgba(212,181,140,0.25)"
                    width={36}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#15102A",
                      border: "1px solid rgba(212,181,140,0.32)",
                      borderRadius: 2,
                      color: "#F4EEDA",
                      fontSize: 12,
                    }}
                    formatter={(v) => [`$${(v || 0).toFixed(2)}`, "Revenue"]}
                  />
                  <Bar dataKey="revenueUsd" fill="#D4B58C" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5 text-xs">
              {data.serviceFunnel.map((s) => (
                <div key={s.serviceId} className="flex items-center justify-between gap-3 py-1 border-b border-foreground/8 last:border-0">
                  <span className="text-foreground/85 truncate">{s.title}</span>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-foreground/55">{s.bookings} booking{s.bookings === 1 ? "" : "s"}</span>
                    <span className="font-serif text-primary tabular-nums">${s.revenueUsd.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </ChartCard>

      {/* ── Recommend next boost ───────────────────────────────── */}
      {data.recommendedBoost && (
        <RecommendNextBoostCard
          rec={data.recommendedBoost}
          onAccept={() => setBoostTarget({
            service: { id: data.recommendedBoost.serviceId, title: data.recommendedBoost.serviceTitle },
            initialTierId: data.recommendedBoost.tierId,
          })}
        />
      )}

      {/* ── Per-boost ROI ───────────────────────────────────────── */}
      <ChartCard
        icon={<Zap size={13} className="text-secondary" />}
        title="Boost ROI"
        subtitle="How each paid boost performed vs. your 7-day baseline"
        testid="pi-boost-roi"
      >
        {(!data.boostROI || data.boostROI.length === 0) ? (
          <EmptyHint>
            No boosts yet in this window. Try a $5 / 24-hour boost on a service
            from your Profile → Services tab.
          </EmptyHint>
        ) : (
          <BoostROITable rows={data.boostROI} />
        )}
      </ChartCard>

      <BoostDialog
        service={boostTarget?.service}
        open={!!boostTarget}
        initialTierId={boostTarget?.initialTierId}
        onClose={() => setBoostTarget(null)}
        onBoosted={() => setBoostTarget(null)}
      />
    </div>
  );
}

function RecommendNextBoostCard({ rec, onAccept }) {
  const isRepeat = rec.kind === "repeat";
  return (
    <div
      className="parchment p-4 border-secondary/40 bg-gradient-to-br from-secondary/[0.08] to-transparent"
      data-testid="pi-recommend-next-boost"
    >
      <div className="flex items-start gap-3">
        <Lightbulb size={16} className="text-secondary shrink-0 mt-0.5" strokeWidth={1.75} />
        <div className="flex-1 min-w-0">
          <div className="label-mystic text-secondary mb-1">
            {isRepeat ? "Recommended next boost" : "Try your first boost"}
          </div>
          <p className="text-sm text-foreground/85 leading-relaxed">
            {rec.reason}
          </p>
          <div className="text-[11px] text-foreground/55 mt-2">
            Suggested:{" "}
            <span className="text-foreground">“{rec.serviceTitle}” · {rec.label}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onAccept}
          className="px-4 py-2 rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/90 text-xs uppercase tracking-[0.22em] font-medium shrink-0 inline-flex items-center gap-1.5"
          data-testid="pi-recommend-next-boost-btn"
        >
          {isRepeat ? "Repeat" : "Try"} <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

function BoostROITable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-foreground/55 uppercase tracking-wider text-[10px]">
            <th className="text-left py-2 pr-3 font-normal">Service</th>
            <th className="text-left py-2 pr-3 font-normal">Tier</th>
            <th className="text-right py-2 pr-3 font-normal">Views</th>
            <th className="text-right py-2 pr-3 font-normal">Lift</th>
            <th className="text-right py-2 pr-3 font-normal">Bookings</th>
            <th className="text-right py-2 pr-3 font-normal">Revenue</th>
            <th className="text-right py-2 font-normal">ROI</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-foreground/8" data-testid={`boost-roi-row-${r.id}`}>
              <td className="py-2.5 pr-3 text-foreground/85 truncate max-w-[160px]">
                {r.serviceTitle}
              </td>
              <td className="py-2.5 pr-3 text-foreground/60">
                {r.label}
                <div className="text-[10px] text-foreground/40">
                  {r.paidWith === "usd" ? `$${r.cost?.toFixed?.(0)}` : `${r.cost} CAUL`}
                </div>
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{r.viewsDuring}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">
                {r.viewsLiftPct === null || r.viewsLiftPct === undefined ? (
                  <span className="text-foreground/40">—</span>
                ) : r.viewsLiftPct >= 0 ? (
                  <span className="text-emerald-400 inline-flex items-center gap-1 justify-end">
                    <TrendingUp size={10} />+{r.viewsLiftPct}%
                  </span>
                ) : (
                  <span className="text-rose-400 inline-flex items-center gap-1 justify-end">
                    <TrendingDown size={10} />{r.viewsLiftPct}%
                  </span>
                )}
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{r.attributedBookings}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-primary">
                ${(r.attributedRevenueUsd || 0).toFixed(2)}
              </td>
              <td className="py-2.5 text-right tabular-nums">
                {r.roiPct === null || r.roiPct === undefined ? (
                  <span className="text-foreground/40" title="CAUL-paid boost — ROI shown in revenue only">—</span>
                ) : r.roiPct >= 0 ? (
                  <span className="text-emerald-400">+{r.roiPct}%</span>
                ) : (
                  <span className="text-rose-400">{r.roiPct}%</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-foreground/40 mt-2 leading-relaxed">
        Lift = views during boost vs. your 7-day baseline projected over the same duration.
        Revenue counts paid bookings of the boosted service inside the boost window.
        ROI compares revenue against USD spent (CAUL-paid boosts have no $ cost basis).
      </div>
    </div>
  );
}

function ChartCard({ icon, title, subtitle, children, testid }) {
  return (
    <div className="parchment p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="label-mystic text-primary">{title}</span>
      </div>
      {subtitle && <div className="text-[11px] text-foreground/55 mb-3">{subtitle}</div>}
      {children}
    </div>
  );
}

function EmptyHint({ children }) {
  return <div className="text-xs text-foreground/55 italic py-2">{children}</div>;
}
